import React, { useEffect, useRef } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import { QueryClientProvider } from '@tanstack/react-query';
import { useStore } from './store';
import { queryClient } from './services/queryClient';
import { useShallow } from 'zustand/react/shallow';
import { ToastProvider } from './components/ToastProvider';
import SentryErrorBoundary from './components/SentryErrorBoundary';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import SignedOutPage from './pages/SignedOutPage';
import Patients from './pages/PatientList';
import PatientDetail from './pages/PatientDetail';
import DocumentViewer from './pages/DocumentViewer';
import Documents from './pages/Documents';
import Tasks from './pages/Tasks';
import IntakeManager from './pages/IntakeManager';
import Intake3 from './pages/Intake3';
import Billing from './pages/Billing/Billing';
import Orders from './pages/Orders';
import AuthManager from './pages/AuthManager';
import ReportsLanding from './pages/ReportsLanding';
import ReportBuilder from './pages/ReportBuilder';
import AdoptionReportPage from './pages/AdoptionReportPage';
import CensusReport from './pages/CensusReport';
import PcrReport from './pages/PcrReport';
import ScheduleReport from './pages/ScheduleReport';
import ScheduleDeviationReport from './pages/ScheduleDeviationReport';
import PatientDiagnosesReport from './pages/PatientDiagnosesReport';
import PatientRosterReport from './pages/PatientRosterReport';
import PastDueVisitsReport from './pages/PastDueVisitsReport';
import WorklogReport from './pages/WorklogReport';
import WoundStatusReport from './pages/WoundStatusReport';
import MarketingReport from './pages/MarketingReport';
import LupaRiskReport from './pages/LupaRiskReport';
import UserOnboardingReport from './pages/UserOnboardingReport';
import Admin from './pages/Admin';
import InternalAdmin from './pages/InternalAdmin';
import Testing from './pages/Testing';
import TestingX12_270 from './pages/TestingX12_270';
import TestingX12_837 from './pages/TestingX12_837';
import TestingX12_835 from './pages/TestingX12_835';
import TestingStedi_270 from './pages/TestingStedi_270';
import SentryTest from './components/SentryTest';
import TestingStedi_COB from './pages/TestingStedi_COB';
import TestingStedi_837I from './pages/TestingStedi_837I';
import ClinicianManager from './pages/ClinicianManager';
import RogerDocOverview from './pages/RogerDocOverview';
import RogerDocUsers from './pages/RogerDocUsers';
import RogerDocUsage from './pages/RogerDocUsage';
import QA from './pages/QA';
import Hotbox from './pages/Hotbox';
import CodingSearch from './pages/CodingSearch';
import CodingDiagnosis from './pages/CodingDiagnosis';
import WorksheetGoals from './pages/WorksheetGoals';
import Resources from './pages/Resources';
import Customizations from './pages/Customizations';
import FormSchemas from './pages/FormSchemas';
import RolesManager from './pages/RBAC/RolesManager';
import InternalTools from './pages/internal/InternalTools';
import NOAManager from './pages/NOAManager';
import OASISManager from './pages/OASISManager';
import OpenOasisReport from './pages/OpenOasisReport';
import CAHPSManager from './pages/CAHPSManager';
import PayrollManager from './pages/PayrollManager';
import PayrollDetail from './pages/PayrollDetail';
import Layout from './components/Layout';
import RogerEmrRouteGuard from './components/RogerEmrRouteGuard';
import { AUTH_ME_REFRESH_INTERVAL_MS } from './store/slices/authSlice';
import { getDefaultLandingPath } from './utils/defaultLandingPath';
import {
  clearPostLoginRedirect,
  currentPathForRedirect,
  rememberPostLoginRedirect,
} from './utils/postLoginRedirect';
import { FeatureFlag } from './constants/featureFlags';
const Dashboard = React.lazy(() => import('./pages/Dashboard'));

/** Wrap RogerEMR-only route elements. Omit for pages that stay open without RogerEMR. */
const requireRogerEmr = (element: React.ReactNode) => (
  <RogerEmrRouteGuard>{element}</RogerEmrRouteGuard>
);

const DashboardLoadingFallback: React.FC = () => (
  <div className='container mx-auto px-4 py-6'>
    <div role='status' className='text-sm text-muted-foreground'>
      Loading dashboard…
    </div>
  </div>
);

const DashboardErrorFallback: React.FC<{
  error: Error;
  resetError: () => void;
}> = () => (
  <div className='container mx-auto px-4 py-6'>
    <div className='rounded-md border border-red-200 bg-red-50 p-4'>
      <h2 className='font-semibold text-red-900'>Dashboard couldn't load</h2>
      <p className='mt-1 text-sm text-red-800'>
        Refresh the page to try again.
      </p>
      <button
        type='button'
        className='mt-3 rounded-md bg-red-700 px-3 py-2 text-sm font-medium text-white hover:bg-red-800'
        onClick={() => window.location.reload()}
      >
        Refresh page
      </button>
    </div>
  </div>
);

const DefaultLandingRoute: React.FC = () => {
  const { user, hasPermission, hasAnyPermission, hasFeatureFlag } = useStore(
    useShallow(state => ({
      user: state.user,
      hasPermission: state.hasPermission,
      hasAnyPermission: state.hasAnyPermission,
      hasFeatureFlag: state.hasFeatureFlag,
    }))
  );

  return (
    <Navigate
      to={getDefaultLandingPath({
        hasPermission,
        hasAnyPermission,
        hasFeatureFlag,
        isRogerEmrEnabled: user?.agency?.is_rogeremr_enabled === true,
      })}
      replace
    />
  );
};

const ReportingFoundationDemo = import.meta.env.DEV
  ? React.lazy(() => import('./pages/ReportingFoundationDemo'))
  : null;

const ReportStateDemo = import.meta.env.DEV
  ? React.lazy(() => import('./pages/dev/ReportStateDemo'))
  : null;

const ReportingRedirect: React.FC = () => {
  const location = useLocation();
  const suffix = location.pathname.slice('/reporting'.length);
  return (
    <Navigate
      to={`/reports${suffix}${location.search}${location.hash}`}
      replace
    />
  );
};

const AdvancedReportsRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const hasAdvancedReporting = useStore(state =>
    state.hasFeatureFlag(FeatureFlag.ADVANCED_REPORTING, 'workos')
  );
  return hasAdvancedReporting ? (
    <>{children}</>
  ) : (
    <Navigate to='/reports' replace />
  );
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, authChecked, checkAuthStatus, refreshCurrentUser } =
    useStore(
      useShallow(state => ({
        isAuthenticated: state.isAuthenticated,
        authChecked: state.authChecked,
        checkAuthStatus: state.checkAuthStatus,
        refreshCurrentUser: state.refreshCurrentUser,
      }))
    );
  const authCheckedRef = useRef(false);

  useEffect(() => {
    // Only check authentication status once on app load
    if (!authCheckedRef.current) {
      authCheckedRef.current = true;
      checkAuthStatus();
    }
  }, [checkAuthStatus]);

  // Periodically refetch /auth/me so WorkOS feature flags stay in sync with the
  // server (~2 min cache). Also refresh when the tab becomes visible again.
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        void refreshCurrentUser();
      }
    };

    document.addEventListener('visibilitychange', refreshWhenVisible);
    const intervalId = setInterval(
      refreshWhenVisible,
      AUTH_ME_REFRESH_INTERVAL_MS
    );

    return () => {
      document.removeEventListener('visibilitychange', refreshWhenVisible);
      clearInterval(intervalId);
    };
  }, [isAuthenticated, refreshCurrentUser]);

  // Once the user is through, forget any path stashed on the way in (EMR-1159).
  useEffect(() => {
    if (authChecked && isAuthenticated) {
      clearPostLoginRedirect();
    }
  }, [authChecked, isAuthenticated]);

  // Show loading while checking auth status
  if (!authChecked) {
    return (
      <div className='min-h-screen bg-background flex items-center justify-center'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Keep the destination so login can return to it instead of the default
    // landing page — deep links are useless otherwise (EMR-1159).
    rememberPostLoginRedirect(currentPathForRedirect());
    return <Navigate to='/login' replace />;
  }

  return <>{children}</>;
};

// App Component
const App: React.FC = () => {
  return (
    <SentryErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <Router>
            <NuqsAdapter>
              <Routes>
                <Route path='/login' element={<LoginPage />} />
                <Route path='/auth/callback' element={<AuthCallback />} />
                <Route path='/signed-out' element={<SignedOutPage />} />
                <Route
                  path='/'
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route index element={<DefaultLandingRoute />} />
                  <Route
                    path='dashboard'
                    element={requireRogerEmr(
                      <SentryErrorBoundary fallback={DashboardErrorFallback}>
                        <React.Suspense fallback={<DashboardLoadingFallback />}>
                          <Dashboard />
                        </React.Suspense>
                      </SentryErrorBoundary>
                    )}
                  />
                  <Route
                    path='patients'
                    element={requireRogerEmr(<Patients />)}
                  />
                  <Route
                    path='patients/:id/:tab?'
                    element={requireRogerEmr(<PatientDetail />)}
                  />
                  <Route
                    path='patients/:patientId/documents/:documentId'
                    element={requireRogerEmr(<DocumentViewer />)}
                  />
                  <Route
                    path='patients/:id/visits/:visitId'
                    element={requireRogerEmr(<PatientDetail />)}
                  />
                  <Route
                    path='documents'
                    element={requireRogerEmr(<Documents />)}
                  />
                  <Route path='tasks' element={requireRogerEmr(<Tasks />)} />
                  <Route path='intake/:intakeId' element={<Intake3 />} />
                  <Route path='intake-manager' element={<IntakeManager />} />
                  <Route
                    path='billing'
                    element={requireRogerEmr(<Billing />)}
                  />
                  <Route path='orders' element={requireRogerEmr(<Orders />)} />
                  <Route
                    path='auth-manager'
                    element={requireRogerEmr(<AuthManager />)}
                  />
                  <Route path='reporting' element={<ReportingRedirect />} />
                  <Route
                    path='reporting/adoption'
                    element={<AdoptionReportPage />}
                  />
                  <Route path='reporting/census' element={<CensusReport />} />
                  <Route
                    path='reporting/schedule'
                    element={<ScheduleReport />}
                  />
                  <Route
                    path='reporting/schedule-deviation'
                    element={<ScheduleDeviationReport />}
                  />
                  <Route
                    path='reporting/patient-diagnoses'
                    element={<PatientDiagnosesReport />}
                  />
                  <Route
                    path='reporting/patient-roster'
                    element={<PatientRosterReport />}
                  />
                  <Route
                    path='reporting/open-oasis'
                    element={<OpenOasisReport />}
                  />
                  <Route path='reporting/worklog' element={<WorklogReport />} />
                  <Route
                    path='reporting/past-due-visits'
                    element={<PastDueVisitsReport />}
                  />
                  <Route
                    path='reporting/wound-status'
                    element={<WoundStatusReport />}
                  />
                  <Route
                    path='reporting/marketing'
                    element={<MarketingReport />}
                  />
                  <Route
                    path='reporting/lupa-risk'
                    element={<LupaRiskReport />}
                  />
                  <Route
                    path='reporting/user-onboarding'
                    element={<UserOnboardingReport />}
                  />
                  <Route path='reporting/*' element={<ReportingRedirect />} />
                  <Route path='reports' element={<ReportsLanding />} />
                  <Route
                    path='reports/adoption'
                    element={<AdoptionReportPage />}
                  />
                  <Route path='reports/census' element={<CensusReport />} />
                  <Route
                    path='reports/pcr'
                    element={requireRogerEmr(<PcrReport />)}
                  />
                  <Route path='reports/schedule' element={<ScheduleReport />} />
                  <Route
                    path='reports/schedule-deviation'
                    element={<ScheduleDeviationReport />}
                  />
                  <Route
                    path='reports/patient-diagnoses'
                    element={<PatientDiagnosesReport />}
                  />
                  <Route
                    path='reports/patient-roster'
                    element={<PatientRosterReport />}
                  />
                  <Route path='reports/worklog' element={<WorklogReport />} />
                  <Route
                    path='reports/past-due-visits'
                    element={<PastDueVisitsReport />}
                  />
                  <Route
                    path='reports/wound-status'
                    element={<WoundStatusReport />}
                  />
                  <Route
                    path='reports/marketing'
                    element={<MarketingReport />}
                  />
                  <Route
                    path='reports/lupa-risk'
                    element={<LupaRiskReport />}
                  />
                  <Route
                    path='reports/user-onboarding'
                    element={<UserOnboardingReport />}
                  />
                  {ReportingFoundationDemo ? (
                    <Route
                      path='reports/foundation-demo'
                      element={
                        <React.Suspense fallback={null}>
                          <ReportingFoundationDemo />
                        </React.Suspense>
                      }
                    />
                  ) : null}
                  <Route
                    path='reports/builder'
                    element={
                      <AdvancedReportsRoute>
                        <ReportBuilder />
                      </AdvancedReportsRoute>
                    }
                  />
                  <Route path='admin' element={<Admin />} />
                  <Route path='internal-admin' element={<InternalAdmin />} />
                  <Route path='user-manager' element={<ClinicianManager />} />
                  <Route
                    path='rogerdoc/overview'
                    element={<RogerDocOverview />}
                  />
                  <Route path='rogerdoc/users' element={<RogerDocUsers />} />
                  <Route path='rogerdoc/usage' element={<RogerDocUsage />} />
                  <Route path='testing' element={<Testing />} />
                  <Route path='testing/x12-270' element={<TestingX12_270 />} />
                  <Route path='testing/x12-837' element={<TestingX12_837 />} />
                  <Route path='testing/x12-835' element={<TestingX12_835 />} />
                  <Route
                    path='testing/stedi-270'
                    element={<TestingStedi_270 />}
                  />
                  <Route path='testing/sentry' element={<SentryTest />} />
                  <Route
                    path='testing/stedi-cob'
                    element={<TestingStedi_COB />}
                  />
                  <Route
                    path='testing/stedi-837i'
                    element={<TestingStedi_837I />}
                  />
                  <Route path='qa' element={requireRogerEmr(<QA />)} />
                  <Route path='qa/:visitId' element={requireRogerEmr(<QA />)} />
                  <Route path='hotbox' element={requireRogerEmr(<Hotbox />)} />
                  <Route path='coding' element={<CodingSearch />} />
                  <Route
                    path='coding/:diagnosisId'
                    element={<CodingDiagnosis />}
                  />
                  <Route
                    path='coding/:diagnosisId/states/:stateId'
                    element={<CodingDiagnosis />}
                  />
                  <Route path='worksheet-goals' element={<WorksheetGoals />} />
                  <Route path='customizations' element={<Customizations />} />
                  <Route path='form-schemas' element={<FormSchemas />} />
                  <Route path='roles-manager' element={<RolesManager />} />
                  <Route path='internal-tools' element={<InternalTools />} />
                  <Route
                    path='noa-manager'
                    element={requireRogerEmr(<NOAManager />)}
                  />
                  <Route
                    path='oasis-manager'
                    element={requireRogerEmr(<OASISManager />)}
                  />
                  <Route
                    path='cahps-manager'
                    element={requireRogerEmr(<CAHPSManager />)}
                  />
                  <Route
                    path='payroll-manager'
                    element={requireRogerEmr(<PayrollManager />)}
                  />
                  <Route
                    path='payroll-manager/:payrollEntryId'
                    element={requireRogerEmr(<PayrollDetail />)}
                  />
                  <Route path='resources' element={<Resources />} />
                  {ReportStateDemo ? (
                    <Route
                      path='dev/report-state-demo'
                      element={
                        <React.Suspense fallback={null}>
                          <ReportStateDemo />
                        </React.Suspense>
                      }
                    />
                  ) : null}
                </Route>
              </Routes>
            </NuqsAdapter>
          </Router>
        </ToastProvider>
      </QueryClientProvider>
    </SentryErrorBoundary>
  );
};

export default App;
